import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { isColorDark } from '../utils';

const API = process.env.REACT_APP_BACKEND_URL;

const getYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
};

const HeroBannerSection = ({ content, accentColor, isDark }) => {
  const bgColor = content.bgColor || accentColor;
  const btnColor = content.buttonColor || '#FFFFFF';
  // Determine if button text color should be dark or light based on button background
  const btnTextDark = (() => {
    const c = btnColor.replace('#', '');
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 128;
  })();

  const renderButton = () => {
    if (!content.buttonText) return null;
    const btn = (
      <button
        className="px-6 py-2.5 rounded-full font-semibold text-sm inline-block mt-4 transition-all hover:opacity-90 hover:scale-105 active:scale-95"
        style={{ backgroundColor: btnColor, color: btnTextDark ? '#000' : '#fff' }}
        onClick={content.buttonUrl ? () => window.open(content.buttonUrl, '_blank') : undefined}
        data-testid="hero-banner-button"
      >
        {content.buttonText}
      </button>
    );
    return btn;
  };

  return (
    <section className="relative w-full overflow-hidden rounded-2xl" data-testid="section-hero-banner">
      {content.imageUrl ? (
        <div className="relative">
          <img src={`${API}${content.imageUrl}`} alt={content.title || ''} className="w-full h-48 sm:h-64 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6">
            {content.title && <h2 className="text-2xl sm:text-3xl font-bold text-white">{content.title}</h2>}
            {content.subtitle && <p className="text-white/80 mt-1">{content.subtitle}</p>}
            {renderButton()}
          </div>
        </div>
      ) : (
        <div className="py-12 px-6 text-center rounded-2xl" style={{ backgroundColor: bgColor }}>
          {content.title && <h2 className="text-2xl sm:text-3xl font-bold text-white">{content.title}</h2>}
          {content.subtitle && <p className="text-white/80 mt-2">{content.subtitle}</p>}
          {renderButton()}
        </div>
      )}
    </section>
  );
};

const TextSection = ({ content, textColor, mutedText }) => (
  <section className="py-4" data-testid="section-text">
    {content.title && <h2 className="text-xl font-bold mb-2" style={{ color: textColor }}>{content.title}</h2>}
    {content.description && <p className="leading-relaxed whitespace-pre-line" style={{ color: mutedText }}>{content.description}</p>}
  </section>
);

const ProductsSection = ({ content, allProducts, slug, accentColor, cardBg, isDark, formatPrice, shopName }) => {
  const navigate = useNavigate();
  const selected = (content.productIds || []).map(id => allProducts.find(p => p.id === id)).filter(Boolean);
  if (selected.length === 0) return null;

  return (
    <section className="py-4" data-testid="section-products">
      {content.title && <h2 className="text-xl font-bold mb-4">{content.title}</h2>}
      <div className="grid grid-cols-2 gap-3">
        {selected.map(product => {
          const img = product.images?.[0];
          return (
            <div
              key={product.id}
              onClick={() => navigate(`/s/${slug}/p/${product.id}`)}
              className="rounded-xl overflow-hidden border cursor-pointer transition-all hover:shadow-md"
              style={{ backgroundColor: cardBg, borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }}
              data-testid={`ws-product-${product.id}`}
            >
              {img ? (
                <img src={`${API}${img.url}`} alt={product.name} className="w-full h-32 object-contain" loading="lazy" />
              ) : (
                <div className="w-full h-32 flex items-center justify-center" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' }}>
                  <span className="text-3xl opacity-20">?</span>
                </div>
              )}
              <div className="p-3">
                <h3 className="font-semibold text-sm line-clamp-1">{product.name}</h3>
                {product.priceCents && (
                  <p className="text-sm font-bold mt-1" style={{ color: accentColor }}>{formatPrice(product.priceCents)}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const CategoriesSection = ({ content, allCategories, allProducts, slug, accentColor, cardBg, isDark }) => {
  const navigate = useNavigate();
  const selected = (content.categoryIds || []).map(id => allCategories.find(c => c.id === id)).filter(Boolean);
  if (selected.length === 0) return null;

  return (
    <section className="py-4" data-testid="section-categories">
      {content.title && <h2 className="text-xl font-bold mb-4">{content.title}</h2>}
      <div className="grid grid-cols-2 gap-3">
        {selected.map(cat => {
          const catProducts = allProducts.filter(p => p.categoryId === cat.id);
          const firstImg = catProducts.find(p => p.images?.[0])?.images?.[0];
          return (
            <div
              key={cat.id}
              onClick={() => navigate(`/s/${slug}/c/${cat.id}`)}
              className="rounded-xl overflow-hidden border cursor-pointer transition-all hover:shadow-md"
              style={{ backgroundColor: cardBg, borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }}
              data-testid={`ws-category-${cat.id}`}
            >
              {firstImg ? (
                <img src={`${API}${firstImg.url}`} alt={cat.name} className="w-full h-28 object-cover" loading="lazy" />
              ) : (
                <div className="w-full h-28 flex items-center justify-center" style={{ backgroundColor: accentColor + '15' }}>
                  <span className="text-2xl font-bold" style={{ color: accentColor }}>{cat.name.charAt(0)}</span>
                </div>
              )}
              <div className="p-3">
                <h3 className="font-semibold text-sm">{cat.name}</h3>
                <p className="text-xs opacity-50 mt-0.5">{catProducts.length} produit(s)</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const VideoSection = ({ content, textColor }) => {
  const ytId = getYouTubeId(content.videoUrl);
  if (!ytId) return null;

  return (
    <section className="py-4" data-testid="section-video">
      {content.title && <h2 className="text-xl font-bold mb-4" style={{ color: textColor }}>{content.title}</h2>}
      <div className="relative w-full rounded-xl overflow-hidden" style={{ paddingBottom: '56.25%' }}>
        <iframe
          src={`https://www.youtube.com/embed/${ytId}`}
          title={content.title || 'Video'}
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </section>
  );
};

const FaqSection = ({ content, textColor, mutedText, cardBg, isDark, accentColor }) => {
  const [open, setOpen] = useState({});
  const items = content.faqItems || [];
  if (items.length === 0) return null;

  return (
    <section className="py-4" data-testid="section-faq">
      {content.title && <h2 className="text-xl font-bold mb-4" style={{ color: textColor }}>{content.title}</h2>}
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="rounded-xl border overflow-hidden" style={{ backgroundColor: cardBg, borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }}>
            <button
              onClick={() => setOpen(prev => ({ ...prev, [i]: !prev[i] }))}
              className="w-full flex items-center justify-between p-4 text-left"
              data-testid={`faq-item-${i}`}
            >
              <span className="font-medium text-sm pr-4" style={{ color: textColor }}>{item.question}</span>
              {open[i] ? <ChevronUp className="w-4 h-4 shrink-0" style={{ color: accentColor }} /> : <ChevronDown className="w-4 h-4 shrink-0" style={{ color: accentColor }} />}
            </button>
            {open[i] && (
              <div className="px-4 pb-4">
                <p className="text-sm whitespace-pre-line" style={{ color: mutedText }}>{item.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

const ImageSliderSection = ({ content, accentColor }) => {
  const imageList = content.images || [];
  const [current, setCurrent] = useState(0);
  const count = imageList.length;

  const goNext = useCallback(() => {
    setCurrent(prev => (prev + 1) % count);
  }, [count]);

  const goPrev = useCallback(() => {
    setCurrent(prev => (prev - 1 + count) % count);
  }, [count]);

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(goNext, 5000);
    return () => clearInterval(timer);
  }, [count, goNext]);

  if (count === 0) return null;

  if (count === 1) {
    return (
      <section className="py-4" data-testid="section-image-slider">
        <img src={`${API}${imageList[0]}`} alt="" className="w-full rounded-2xl object-cover" style={{ height: '240px' }} />
      </section>
    );
  }

  return (
    <section className="py-4" data-testid="section-image-slider">
      <div className="relative overflow-hidden rounded-2xl">
        <img src={`${API}${imageList[current]}`} alt="" className="w-full object-cover transition-opacity duration-500" style={{ height: '240px' }} />
        <button onClick={goPrev} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/60 transition-colors" data-testid="slider-prev">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button onClick={goNext} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/60 transition-colors" data-testid="slider-next">
          <ChevronRight className="w-5 h-5" />
        </button>
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
          {imageList.map(function(_, idx) {
            var isActive = idx === current;
            return (
              <button key={idx} onClick={function() { setCurrent(idx); }} className="transition-all" style={{ width: isActive ? '24px' : '8px', height: '8px', borderRadius: '4px', backgroundColor: isActive ? accentColor : 'rgba(255,255,255,0.5)' }} data-testid={`slider-dot-${idx}`} />
            );
          })}
        </div>
      </div>
    </section>
  );
};

const ImageTextSection = ({ content, accentColor, isDark }) => {
  const pos = content.imagePosition || 'right';
  const textColor = isDark ? '#fff' : '#1a1a1a';
  const subtitleColor = isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)';
  const descColor = isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.75)';
  const imgUrl = content.imageUrl ? `${API}${content.imageUrl}` : null;

  const imageBlock = imgUrl ? (
    <div className="w-full lg:w-1/2 shrink-0">
      <img src={imgUrl} alt="" className="w-full h-64 lg:h-80 object-cover rounded-2xl" data-testid="image-text-img" />
    </div>
  ) : null;

  const textBlock = (
    <div className="w-full lg:w-1/2 flex flex-col justify-center py-4 lg:py-0 text-center" data-testid="image-text-content">
      {content.title && (
        <h2 className="text-2xl lg:text-3xl font-bold mb-2" style={{ color: textColor }}>{content.title}</h2>
      )}
      {content.subtitle && (
        <p className="text-base lg:text-lg mb-4" style={{ color: subtitleColor }}>{content.subtitle}</p>
      )}
      {content.description && (
        <p className="text-sm lg:text-base leading-relaxed mb-6" style={{ color: descColor }}>{content.description}</p>
      )}
      {content.buttonText && (
        <div className="mx-auto">
          <a
            href={content.buttonUrl || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-6 py-3 rounded-lg font-medium text-sm transition-transform hover:scale-105"
            style={{ backgroundColor: content.buttonColor || '#000', color: isColorDark(content.buttonColor || '#000') ? '#fff' : '#000' }}
            data-testid="image-text-btn"
          >
            {content.buttonText}
          </a>
        </div>
      )}
    </div>
  );

  return (
    <section className="py-6" data-testid="section-image-text">
      <div className={`flex flex-col lg:flex-row gap-6 lg:gap-10 items-center ${pos === 'left' ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
        {imageBlock}
        {textBlock}
      </div>
    </section>
  );
};

const OpeningHoursSection = ({ content, textColor, mutedText, cardBg, isDark, accentColor }) => {
  const schedule = content.schedule || [];
  const now = new Date();
  const dayIndex = (now.getDay() + 6) % 7;
  const currentTime = String(now.getHours()).padStart(2, '0') + ':' + String(now.getMinutes()).padStart(2, '0');

  return (
    <section className="w-full" data-testid="section-opening-hours">
      {content.title && <h2 className="text-xl font-bold mb-4 text-center" style={{ color: textColor }}>{content.title}</h2>}
      <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: cardBg }}>
        {schedule.map(function(item, idx) {
          var isToday = idx === dayIndex;
          var isClosed = item.isClosed === 'true';
          var isOpen = isToday && !isClosed && currentTime >= item.openTime && currentTime <= item.closeTime;
          return (
            <div key={idx} className={'flex items-center justify-between px-4 py-3 ' + (isToday ? 'font-semibold' : '') + (idx < schedule.length - 1 ? ' border-b' : '')} style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }}>
              <div className="flex items-center gap-2">
                <span style={{ color: isToday ? textColor : mutedText }}>{item.day}</span>
                {isOpen && <span className="px-2 py-0.5 rounded-full text-xs font-bold text-white" style={{ backgroundColor: '#22C55E' }}>Ouvert</span>}
                {isToday && isClosed && <span className="px-2 py-0.5 rounded-full text-xs font-bold text-white bg-red-500">Ferme</span>}
              </div>
              <span style={{ color: isClosed ? mutedText : textColor }}>{isClosed ? 'Ferme' : item.openTime + ' - ' + item.closeTime}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const GoogleMapSection = ({ content, textColor, shopAddress }) => {
  var query = content.mapQuery || content.description || shopAddress || '';
  var encodedQuery = encodeURIComponent(query);
  return (
    <section className="w-full" data-testid="section-google-map">
      {content.title && <h2 className="text-xl font-bold mb-4 text-center" style={{ color: textColor }}>{content.title}</h2>}
      {content.description && <p className="text-center mb-4 text-sm opacity-70" style={{ color: textColor }}>{content.description}</p>}
      <div className="rounded-2xl overflow-hidden" style={{ height: '300px' }}>
        <iframe
          title="Google Maps"
          width="100%"
          height="100%"
          frameBorder="0"
          style={{ border: 0 }}
          src={'https://maps.google.com/maps?q=' + encodedQuery + '&t=&z=15&ie=UTF8&iwloc=&output=embed'}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
    </section>
  );
};

const BeforeAfterSection = ({ content, textColor, isDark }) => {
  var sliderRef = React.useRef(null);
  var containerRef = React.useRef(null);
  var isDraggingRef = React.useRef(false);
  var posRef = React.useRef(50);
  var frameRef = React.useRef(null);

  var updateSlider = function(clientX) {
    if (!containerRef.current) return;
    var rect = containerRef.current.getBoundingClientRect();
    var x = clientX - rect.left;
    var pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    posRef.current = pct;
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(function() {
      if (sliderRef.current) {
        sliderRef.current.style.left = pct + '%';
      }
      var clipEl = containerRef.current && containerRef.current.querySelector('[data-after-clip]');
      if (clipEl) {
        clipEl.style.clipPath = 'inset(0 0 0 ' + pct + '%)';
      }
    });
  };

  React.useEffect(function() {
    var handleMove = function(e) {
      if (!isDraggingRef.current) return;
      e.preventDefault();
      var clientX = e.touches ? e.touches[0].clientX : e.clientX;
      updateSlider(clientX);
    };
    var handleUp = function() { isDraggingRef.current = false; };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchend', handleUp);
    return function() {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleUp);
    };
  }, []);

  if (!content.beforeImage && !content.afterImage) return null;

  return (
    <section className="w-full" data-testid="section-before-after">
      {content.title && <h2 className="text-xl font-bold mb-2 text-center" style={{ color: textColor }}>{content.title}</h2>}
      {content.subtitle && <p className="text-center mb-4 text-sm opacity-70" style={{ color: textColor }}>{content.subtitle}</p>}
      <div ref={containerRef} className="relative rounded-2xl overflow-hidden cursor-ew-resize select-none" style={{ aspectRatio: '16/10' }}
        onMouseDown={function(e) { isDraggingRef.current = true; updateSlider(e.clientX); }}
        onTouchStart={function(e) { isDraggingRef.current = true; updateSlider(e.touches[0].clientX); }}
      >
        {content.beforeImage && <img src={API + content.beforeImage} alt="Avant" className="absolute inset-0 w-full h-full object-cover" />}
        {content.afterImage && (
          <div data-after-clip className="absolute inset-0" style={{ clipPath: 'inset(0 0 0 50%)' }}>
            <img src={API + content.afterImage} alt="Apres" className="w-full h-full object-cover" />
          </div>
        )}
        <div ref={sliderRef} className="absolute top-0 bottom-0 z-10" style={{ left: '50%' }}>
          <div className="absolute top-0 bottom-0 w-0.5 bg-white" style={{ left: '-1px' }} />
          <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M7 4L3 10L7 16" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M13 4L17 10L13 16" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
        </div>
        <div className="absolute bottom-3 left-3 px-2 py-1 rounded-lg text-xs font-bold bg-black/60 text-white z-20">AVANT</div>
        <div className="absolute bottom-3 right-3 px-2 py-1 rounded-lg text-xs font-bold bg-black/60 text-white z-20">APRES</div>
      </div>
    </section>
  );
};

const CountdownTimer = ({ endDate, textColor }) => {
  const [timeLeft, setTimeLeft] = React.useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [expired, setExpired] = React.useState(false);

  React.useEffect(() => {
    if (!endDate) return;
    const update = () => {
      const diff = new Date(endDate).getTime() - Date.now();
      if (diff <= 0) { setExpired(true); return; }
      setTimeLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000)
      });
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [endDate]);

  if (expired) return <p className="text-sm font-medium opacity-80" style={{ color: textColor }}>Offre terminee</p>;
  if (!endDate) return null;

  const units = [
    { val: timeLeft.days, label: 'J' },
    { val: timeLeft.hours, label: 'H' },
    { val: timeLeft.minutes, label: 'M' },
    { val: timeLeft.seconds, label: 'S' }
  ];

  return (
    <div className="flex gap-2" data-testid="promo-countdown">
      {units.map(function(u, i) {
        return (
          <div key={i} className="flex flex-col items-center">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center font-bold text-lg sm:text-xl" style={{ backgroundColor: 'rgba(0,0,0,0.25)', color: textColor }}>
              {String(u.val).padStart(2, '0')}
            </div>
            <span className="text-xs mt-1 opacity-70" style={{ color: textColor }}>{u.label}</span>
          </div>
        );
      })}
    </div>
  );
};

const PromotionSection = ({ content, accentColor, isDark }) => {
  const bgColor = content.bgColor || '#DC2626';
  const textColor = content.textColor || '#FFFFFF';

  return (
    <section className="relative w-full overflow-hidden rounded-2xl" data-testid="section-promotion">
      {content.imageUrl ? (
        <div className="relative">
          <img src={`${API}${content.imageUrl}`} alt={content.title || ''} className="w-full h-auto min-h-[200px] max-h-[350px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
          <div className="absolute inset-0 flex flex-col justify-center p-6 sm:p-8">
            <div className="max-w-md">
              {content.discountText && (
                <span className="inline-block px-3 py-1 rounded-full text-sm font-bold mb-3" style={{ backgroundColor: bgColor, color: textColor }}>
                  {content.discountText}
                </span>
              )}
              {content.title && <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">{content.title}</h2>}
              {content.subtitle && <p className="text-white/90 font-medium mb-2">{content.subtitle}</p>}
              {content.description && <p className="text-white/70 text-sm mb-4">{content.description}</p>}
              <CountdownTimer endDate={content.endDate} textColor="#FFFFFF" />
              {content.buttonText && (
                <button
                  className="mt-4 px-6 py-2.5 rounded-full font-semibold text-sm transition-all hover:opacity-90 hover:scale-105 active:scale-95"
                  style={{ backgroundColor: textColor, color: bgColor }}
                  onClick={content.buttonUrl ? function() { window.open(content.buttonUrl, '_blank'); } : undefined}
                  data-testid="promo-button"
                >
                  {content.buttonText}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="p-6 sm:p-8 text-center" style={{ backgroundColor: bgColor }}>
          {content.discountText && (
            <span className="inline-block px-4 py-1.5 rounded-full text-lg sm:text-2xl font-black mb-3 animate-pulse" style={{ backgroundColor: 'rgba(0,0,0,0.2)', color: textColor }}>
              {content.discountText}
            </span>
          )}
          {content.title && <h2 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: textColor }}>{content.title}</h2>}
          {content.subtitle && <p className="font-medium mb-1 opacity-90" style={{ color: textColor }}>{content.subtitle}</p>}
          {content.description && <p className="text-sm mb-4 opacity-75" style={{ color: textColor }}>{content.description}</p>}
          <div className="flex justify-center mb-4">
            <CountdownTimer endDate={content.endDate} textColor={textColor} />
          </div>
          {content.buttonText && (
            <button
              className="px-6 py-2.5 rounded-full font-semibold text-sm transition-all hover:opacity-90 hover:scale-105 active:scale-95"
              style={{ backgroundColor: textColor, color: bgColor }}
              onClick={content.buttonUrl ? function() { window.open(content.buttonUrl, '_blank'); } : undefined}
              data-testid="promo-button"
            >
              {content.buttonText}
            </button>
          )}
        </div>
      )}
    </section>
  );
};

const WebsiteSectionRenderer = ({ section, allProducts, allCategories, slug, theme, formatPrice, shopName }) => {
  const { accentColor, cardBg, isDark, textColor, mutedText } = theme;
  const content = section.content || {};

  switch (section.type) {
    case 'hero_banner':
      return <HeroBannerSection content={content} accentColor={accentColor} isDark={isDark} />;
    case 'image_slider':
      return <ImageSliderSection content={content} accentColor={accentColor} />;
    case 'image_text':
      return <ImageTextSection content={content} accentColor={accentColor} isDark={isDark} />;
    case 'text':
      return <TextSection content={content} textColor={textColor} mutedText={mutedText} />;
    case 'products':
      return <ProductsSection content={content} allProducts={allProducts} slug={slug} accentColor={accentColor} cardBg={cardBg} isDark={isDark} formatPrice={formatPrice} shopName={shopName} />;
    case 'categories':
      return <CategoriesSection content={content} allCategories={allCategories} allProducts={allProducts} slug={slug} accentColor={accentColor} cardBg={cardBg} isDark={isDark} />;
    case 'video':
      return <VideoSection content={content} textColor={textColor} />;
    case 'faq':
      return <FaqSection content={content} textColor={textColor} mutedText={mutedText} cardBg={cardBg} isDark={isDark} accentColor={accentColor} />;
    case 'promotion':
      return <PromotionSection content={content} accentColor={accentColor} isDark={isDark} />;
    case 'opening_hours':
      return <OpeningHoursSection content={content} textColor={textColor} mutedText={mutedText} cardBg={cardBg} isDark={isDark} accentColor={accentColor} />;
    case 'google_map':
      return <GoogleMapSection content={content} textColor={textColor} shopAddress={shopName} />;
    case 'before_after':
      return <BeforeAfterSection content={content} textColor={textColor} isDark={isDark} />;
    default:
      return null;
  }
};

export default WebsiteSectionRenderer;
